import type { components } from '@/lib/openapi.generated'
import { isRecord, nullableString, request, requireArray, requireBoolean, requireNumber, requireString, requireTimestamp, withQuery } from '@/lib/api'
import { policyResultKeys } from '@/modules/hierarchy'
import { PREFERRED_COUNTER_POLICY_TYPE } from './preferred-counter-policy-api'
import type { PrinterPolicyValueType } from './printer-policy-applicability-api'

type Schemas = components['schemas']
export const PRINTER_POLICY_DEVICES_PAGE_SIZE = 25
export const policyDeviceOutcome = Object.freeze({ Unknown: 0, NonMatch: 1, Used: 2, Replaced: 3, ExplicitlySuppressed: 4 })
export const policyDeviceRefreshStatus = Object.freeze({ Unknown: 0, Pending: 1, Running: 2, Current: 3, Failed: 4 })
export type PrinterPolicyDevice = Schemas['PolicyDeviceResult']
export type PrinterPolicyDevices = Schemas['PolicyDeviceResults']
export type PrinterPolicyDeviceFilter = 'MatchingDevices' | 'PolicyUsed' | 'Overridden' | 'DoesNotMatch' | 'AllAccessibleDevices'
export type PrinterPolicyDevicesCriteria = {
  includeDescendants: boolean
  filter: PrinterPolicyDeviceFilter
  search: string
  after?: string
}

export const printerPolicyDevicesKeys = {
  policy: (entityId: string, valueType: PrinterPolicyValueType, policyId: string) => [
    ...policyResultKeys.type(entityId, valueType), 'devices', policyId,
  ],
}

function integer(raw: unknown, field: string, minimum = 0) {
  const value = requireNumber(raw, field)
  if (!Number.isSafeInteger(value) || value < minimum) throw new Error(`Malformed ${field}`)
  return value
}

function nullableInteger(raw: unknown, field: string) {
  return raw === null ? null : integer(raw, field, 1)
}

function timestamp(raw: unknown, field: string) {
  return raw === null ? null : requireTimestamp(raw, field)
}

function outcome(raw: unknown) {
  const value = requireNumber(raw, 'outcome')
  if (Object.values(policyDeviceOutcome).some((outcome) => outcome === value)) return value
  throw new Error('Malformed policy device outcome')
}

function refreshStatus(raw: unknown) {
  const value = requireNumber(raw, 'refreshStatus')
  if (Object.values(policyDeviceRefreshStatus).some((status) => status === value)) return value
  throw new Error('Malformed policy device refresh status')
}

function failure(raw: unknown) {
  if (raw === null) return null
  if (!isRecord(raw)) throw new Error('Malformed policy device failure')
  return {
    code: requireString(raw.code, 'code'),
    attemptedAt: requireTimestamp(raw.attemptedAt, 'attemptedAt'),
  } satisfies Schemas['PolicyRefreshFailure']
}

function winner(raw: unknown) {
  if (raw === null) return null
  if (!isRecord(raw) || !isRecord(raw.key)) throw new Error('Malformed replacing policy')
  return {
    key: {
      entityId: requireString(raw.key.entityId, 'entityId'),
      policyId: requireString(raw.key.policyId, 'policyId'),
    },
    name: requireString(raw.name, 'name'),
    revision: integer(raw.revision, 'revision', 1),
  } satisfies Schemas['PolicyApplicabilityWinner']
}

function device(raw: unknown): PrinterPolicyDevice {
  if (!isRecord(raw)) throw new Error('Malformed policy device')
  return {
    assetId: requireString(raw.assetId, 'assetId'),
    entityId: requireString(raw.entityId, 'entityId'),
    entity: requireString(raw.entity, 'entity'),
    name: requireString(raw.name, 'name'),
    model: nullableString(raw.model, 'model'),
    lastKnownNameAt: timestamp(raw.lastKnownNameAt, 'lastKnownNameAt'),
    lastKnownModelAt: timestamp(raw.lastKnownModelAt, 'lastKnownModelAt'),
    outcome: outcome(raw.outcome),
    selectionRevision: nullableInteger(raw.selectionRevision, 'selectionRevision'),
    evaluatedAt: timestamp(raw.evaluatedAt, 'evaluatedAt'),
    evaluatedSelectorRevision: nullableInteger(raw.evaluatedSelectorRevision, 'evaluatedSelectorRevision'),
    refreshStatus: refreshStatus(raw.refreshStatus),
    lastFailure: failure(raw.lastFailure),
    replacingPolicy: winner(raw.replacingPolicy),
  } satisfies PrinterPolicyDevice
}

export const printerPolicyDevicesApi = {
  get: (entityId: string, policyId: string, valueType: PrinterPolicyValueType,
    criteria: PrinterPolicyDevicesCriteria, signal?: AbortSignal): Promise<PrinterPolicyDevices> => {
    const replacing = valueType === PREFERRED_COUNTER_POLICY_TYPE
    if (!replacing && criteria.filter === 'Overridden') throw new Error('Event rules do not support the Overridden filter')
    const collection = replacing ? 'printer-counter-policies' : 'printer-event-rule-policies'
    const url = withQuery(`/api/v1/entities/${entityId}/${collection}/${policyId}/devices`, {
      includeDescendants: String(criteria.includeDescendants), filter: criteria.filter,
      search: criteria.search || undefined, limit: String(PRINTER_POLICY_DEVICES_PAGE_SIZE), after: criteria.after,
    })
    return request(url, (raw) => {
      if (!isRecord(raw)) throw new Error('Malformed policy device results')
      if (raw.policyId !== policyId) throw new Error('Device results answered for a different policy')
      const expectedComposition = replacing ? 0 : 1
      if (raw.composition !== expectedComposition) throw new Error('Unexpected policy device composition')
      const items = requireArray(raw.items, 'items', device)
      const totalCount = integer(raw.totalCount, 'totalCount')
      const hasNextPage = requireBoolean(raw.hasNextPage, 'hasNextPage')
      const endCursor = nullableString(raw.endCursor, 'endCursor')
      if (items.length > PRINTER_POLICY_DEVICES_PAGE_SIZE || items.length > totalCount || (hasNextPage && (!endCursor || items.length === 0))) {
        throw new Error('Malformed policy device page')
      }
      return {
        policyId, definitionRevision: integer(raw.definitionRevision, 'definitionRevision', 1),
        composition: expectedComposition, totalCount, items, endCursor, hasNextPage,
        metadataReadAt: requireTimestamp(raw.metadataReadAt, 'metadataReadAt'),
      } satisfies PrinterPolicyDevices
    }, { signal })
  },
}
